// src/admin/components/EnhancedProductDrawer.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Avatar,
  useTheme,
  Backdrop,
  LinearProgress,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  LocationOn as BranchIcon,
  LocalOffer as DiscountIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  Storefront as StorefrontIcon,
  DirectionsCar as DirectionsCarIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import 'quill/dist/quill.snow.css';
import {
  Product,
  ProductFormData,
  Category,
  Warehouse,
  calculateDiscountedPrice,
  calculateDiscountAmount,
  FUEL_TYPES,
  TRANSMISSION_TYPES,
  CAR_CONDITIONS,
  DRIVE_TYPES,
  CAR_STATUSES,
} from '../../../enums/product';
import { CarModel, Trim } from '../carmodel/carModel';
import { ProductService } from '../car/carService';
import { CategoryService } from '../category/categoryService';
import { fetchActiveCarModels } from '../carmodel/carModelService';
import { WarehouseService } from '../branch/branchService';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import { QuickAddCategoryDrawer, QuickAddBrandDrawer, QuickAddModelDrawer } from './QuickAddDrawers';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatNumber } from '../../../util/Formatcurrency';
import { countries } from '../../../enums/countries';
import ImageLightbox from '../../components/ImageLightbox';
import DrawerFooter from '../../components/DrawerFooter';

// ─────────────────────────────────────────────────────────────────────────────
// Rich Text Editor — built directly on quill (no react-quill wrapper)
// ─────────────────────────────────────────────────────────────────────────────
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter description...',
  label,
  required = false,
  error = false,
  helperText,
}) => {
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || quillInstanceRef.current) return;

    import('quill').then(({ default: Quill }) => {
      if (!containerRef.current || quillInstanceRef.current) return;

      const toolbarContainer = containerRef.current.querySelector('.ql-custom-toolbar') as HTMLElement;
      const editorContainer = containerRef.current.querySelector('.ql-custom-editor') as HTMLElement;

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        placeholder,
        modules: {
          toolbar: toolbarContainer,
        },
        formats: [
          'header', 'bold', 'italic', 'underline', 'strike',
          'color', 'background', 'list', 'indent',
          'align', 'blockquote', 'code-block', 'link',
        ],
      });

      if (value) {
        quill.root.innerHTML = value;
      }

      quill.on('text-change', () => {
        const html = quill.root.innerHTML;
        onChangeRef.current(html === '<p><br></p>' ? '' : html);
      });

      quill.root.addEventListener('focus', () => setFocused(true));
      quill.root.addEventListener('blur', () => setFocused(false));

      quillInstanceRef.current = quill;
    });

    return () => {
      if (quillInstanceRef.current) {
        quillInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const quill = quillInstanceRef.current;
    if (!quill) return;
    const currentHtml = quill.root.innerHTML;
    const normalised = currentHtml === '<p><br></p>' ? '' : currentHtml;
    if (normalised !== value) {
      quill.root.innerHTML = value || '';
    }
  }, [value]);

  const borderColor = error
    ? theme.palette.error.main
    : focused
      ? theme.palette.primary.main
      : theme.palette.divider;

  const toolbarBg = theme.palette.background.paper;
  const toolbarBorderColor = theme.palette.divider;
  const toolbarIconColor = theme.palette.text.secondary;
  const toolbarActiveColor = theme.palette.primary.main;
  const editorBg = theme.palette.background.paper;
  const editorTextColor = theme.palette.text.primary;
  const placeholderColor = theme.palette.text.disabled;
  const blockquoteBorderColor = theme.palette.primary.light;
  const blockquoteTextColor = theme.palette.text.secondary;
  const codeBg = isDark ? theme.palette.grey[800] : theme.palette.grey[100];
  const codeTextColor = theme.palette.text.primary;

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="caption"
          component="label"
          sx={{
            display: 'block',
            mb: 0.5,
            color: error ? 'error.main' : focused ? 'primary.main' : 'text.secondary',
            fontWeight: 500,
            fontSize: '0.75rem',
            transition: 'color 0.2s',
          }}
        >
          {label}
          {required && (
            <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      <Box
        ref={containerRef}
        sx={{
          border: '1px solid',
          borderColor,
          borderRadius: 1,
          overflow: 'hidden',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          boxShadow: focused && !error
            ? `0 0 0 2px ${theme.palette.primary.main}20`
            : error && focused
              ? `0 0 0 2px ${theme.palette.error.main}20`
              : 'none',
          '& .ql-toolbar': {
            borderTop: 'none', borderLeft: 'none', borderRight: 'none',
            borderBottom: `1px solid ${toolbarBorderColor}`,
            backgroundColor: toolbarBg, flexWrap: 'wrap',
          },
          '& .ql-toolbar button': { color: toolbarIconColor },
          '& .ql-toolbar button:hover, & .ql-toolbar button.ql-active': { color: toolbarActiveColor },
          '& .ql-toolbar .ql-stroke': { stroke: toolbarIconColor },
          '& .ql-toolbar button:hover .ql-stroke, & .ql-toolbar button.ql-active .ql-stroke': { stroke: toolbarActiveColor },
          '& .ql-toolbar .ql-fill': { fill: toolbarIconColor },
          '& .ql-toolbar button:hover .ql-fill, & .ql-toolbar button.ql-active .ql-fill': { fill: toolbarActiveColor },
          '& .ql-toolbar .ql-picker': { color: toolbarIconColor },
          '& .ql-toolbar .ql-picker-label': { color: toolbarIconColor, borderColor: `${toolbarIconColor}40` },
          '& .ql-toolbar .ql-picker-label:hover': { color: toolbarActiveColor },
          '& .ql-toolbar .ql-picker-options': { backgroundColor: toolbarBg, border: `1px solid ${toolbarBorderColor}`, boxShadow: theme.shadows[4] },
          '& .ql-toolbar .ql-picker-item': { color: editorTextColor },
          '& .ql-toolbar .ql-picker-item:hover': { color: toolbarActiveColor },
          '& .ql-container': { border: 'none', fontFamily: 'inherit', fontSize: '0.875rem', backgroundColor: editorBg },
          '& .ql-editor': {
            minHeight: '120px', maxHeight: '280px', overflowY: 'auto',
            fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.6,
            color: editorTextColor, backgroundColor: editorBg, padding: '10px 14px',
          },
          '& .ql-editor.ql-blank::before': { color: placeholderColor, fontStyle: 'normal', left: '14px' },
          '& .ql-editor h1': { fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px', color: editorTextColor },
          '& .ql-editor h2': { fontSize: '1.25rem', fontWeight: 600, margin: '0 0 6px', color: editorTextColor },
          '& .ql-editor h3': { fontSize: '1.1rem', fontWeight: 600, margin: '0 0 4px', color: editorTextColor },
          '& .ql-editor ul, & .ql-editor ol': { paddingLeft: '24px' },
          '& .ql-editor li': { color: editorTextColor },
          '& .ql-editor p': { color: editorTextColor },
          '& .ql-editor blockquote': {
            borderLeft: `3px solid ${blockquoteBorderColor}`, paddingLeft: '12px',
            color: blockquoteTextColor, fontStyle: 'italic', margin: '8px 0', backgroundColor: 'transparent',
          },
          '& .ql-editor pre': {
            backgroundColor: codeBg, color: codeTextColor, borderRadius: '4px',
            padding: '8px', fontSize: '0.8rem', fontFamily: 'monospace',
          },
          '& .ql-editor code': {
            backgroundColor: codeBg, color: codeTextColor, borderRadius: '3px',
            padding: '1px 4px', fontSize: '0.8rem', fontFamily: 'monospace',
          },
        }}
      >
        <div className="ql-custom-toolbar ql-toolbar ql-snow">
          <span className="ql-formats">
            <select className="ql-header" defaultValue="">
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
              <option value="">Normal</option>
            </select>
          </span>
          <span className="ql-formats">
            <button className="ql-bold" /><button className="ql-italic" />
            <button className="ql-underline" /><button className="ql-strike" />
          </span>
          <span className="ql-formats">
            <select className="ql-color" /><select className="ql-background" />
          </span>
          <span className="ql-formats">
            <button className="ql-list" value="ordered" /><button className="ql-list" value="bullet" />
            <button className="ql-indent" value="-1" /><button className="ql-indent" value="+1" />
          </span>
          <span className="ql-formats"><select className="ql-align" /></span>
          <span className="ql-formats">
            <button className="ql-blockquote" /><button className="ql-code-block" />
          </span>
          <span className="ql-formats"><button className="ql-link" /></span>
          <span className="ql-formats"><button className="ql-clean" /></span>
        </div>
        <div className="ql-custom-editor" />
      </Box>

      {helperText && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mx: '14px', color: error ? 'error.main' : 'text.secondary' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Read-only rich text renderer (view mode)
// ─────────────────────────────────────────────────────────────────────────────
interface RichTextViewerProps {
  value: string;
}

const RichTextViewer: React.FC<RichTextViewerProps> = ({ value }) => {
  const viewerTheme = useTheme();
  const viewerIsDark = viewerTheme.palette.mode === 'dark';
  const codeBg = viewerIsDark ? 'rgba(255,255,255,0.06)' : viewerTheme.palette.grey[100];

  if (!value) return <Typography variant="body2" color="text.secondary">No description provided.</Typography>;

  return (
    <Box
      sx={{
        '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 0, mb: 1, color: 'text.primary' },
        '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 0, mb: 0.75, color: 'text.primary' },
        '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 0, mb: 0.5, color: 'text.primary' },
        '& p': { mt: 0, mb: 0.75, fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary' },
        '& ul, & ol': { pl: 3, mb: 0.75 },
        '& li': { fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary' },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
        '& u': { textDecoration: 'underline' },
        '& s': { textDecoration: 'line-through' },
        '& blockquote': {
          borderLeft: '3px solid', borderColor: 'primary.light', pl: 2, py: 0.5, my: 1,
          color: 'text.secondary', fontStyle: 'italic', fontSize: '0.875rem',
        },
        '& pre': { backgroundColor: codeBg, borderRadius: '4px', p: 1.5, fontSize: '0.8rem', fontFamily: 'monospace', overflowX: 'auto', my: 1, color: 'text.primary' },
        '& code': { backgroundColor: codeBg, borderRadius: '3px', padding: '1px 4px', fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.primary' },
        '& a': { color: 'primary.main', textDecoration: 'underline' },
        '& table': { width: '100%', borderCollapse: 'collapse', my: 1, fontSize: '0.8rem' },
        '& th, & td': { border: '1px solid', borderColor: 'divider', p: '6px 10px' },
        '& th': { bgcolor: 'action.hover', fontWeight: 600 },
      }}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper
// ─────────────────────────────────────────────────────────────────────────────
const isRichTextEmpty = (html: string): boolean => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').trim().length === 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface EnhancedProductDrawerProps {
  isOpen: boolean;
  mode: 'add' | 'edit' | 'view';
  product: Product | null;
  onClose: () => void;
  onSave: (savedProductId?: string) => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  // Seeds the form when opened in 'add' mode (e.g. converting an accepted
  // trade-in into a listing) — ignored in 'edit'/'view' mode.
  initialData?: Partial<ProductFormData>;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: React.ReactNode;
  loading: boolean;
  onConfirm: () => Promise<void>;
}

const CONFIRM_DIALOG_CLOSED: ConfirmDialogState = {
  open: false, title: '', message: '', loading: false, onConfirm: async () => { },
};

const EMPTY_FORM_DATA: ProductFormData = {
  name: '', description: '', price: '', discount: '0',
  categoryId: '', subcategoryId: '', brandId: '', modelId: '', trimId: '', sku: '', images: [],
  branchId: '', unitCost: '0',
  modelYear: '', registrationYear: '', mileageKm: '', fuelType: '', transmission: '',
  engineCapacityCc: '', driveType: '', seatingCapacity: '', exteriorColor: '',
  interiorColor: '', condition: 'Used - Good', status: 'In Stock', registrationNumber: '',
  ownersCount: '', isImported: false, importOrderId: '', countryOfOrigin: '', features: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const EnhancedProductDrawer: React.FC<EnhancedProductDrawerProps> = ({
  isOpen, mode, product, onClose, onSave, onShowToast, initialData,
}) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState<ProductFormData>(EMPTY_FORM_DATA);

  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>(CONFIRM_DIALOG_CLOSED);

  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [branches, setBranches] = useState<Warehouse[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  // ── Quick-add drawers (category / subcategory / brand / model) ───────────
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [subcategoryDrawerOpen, setSubcategoryDrawerOpen] = useState(false);
  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [modelDrawerOpen, setModelDrawerOpen] = useState(false);

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);
  const formatNum = (n: number, decimals = 2) => formatNumber(n, decimals);

  const activeBranches = branches.filter(w => w.isActive);
  // Models are scoped to the selected brand — a car's trim only makes sense within its own model.
  const modelsForBrand = carModels.filter(m => m.brandId === formData.brandId);
  const selectedModel = carModels.find(m => m.id === formData.modelId);
  const trimsForModel: Trim[] = (selectedModel?.trims || []).filter(t => t.isActive);

  // ── Theme-aware tokens ────────────────────────────────────────────────────
  const cardBg = theme.palette.background.paper;
  const subtleBg = isDark ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50];
  const subtlerBg = isDark ? 'rgba(255,255,255,0.07)' : theme.palette.grey[100];
  const successBg = isDark ? 'rgba(76,175,80,0.12)' : 'rgba(76,175,80,0.08)';
  const successBorder = isDark ? 'rgba(76,175,80,0.35)' : 'rgba(76,175,80,0.3)';
  const dividerColor = theme.palette.divider;
  const textSecondary = theme.palette.text.secondary;

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images); setLightboxIndex(index); setLightboxOpen(true);
  };

  // ── Quick-add drawer handlers ─────────────────────────────────────────────
  const openCategoryDrawer = () => {
    if (!currentUser) { onShowToast('You must be logged in', 'error'); return; }
    setCategoryDrawerOpen(true);
  };

  const openSubcategoryDrawer = () => {
    if (!currentUser) { onShowToast('You must be logged in', 'error'); return; }
    if (!formData.categoryId) { onShowToast('Select a main category first', 'warning'); return; }
    setSubcategoryDrawerOpen(true);
  };

  const openBrandDrawer = () => {
    if (!currentUser) { onShowToast('You must be logged in', 'error'); return; }
    setBrandDrawerOpen(true);
  };

  const openModelDrawer = () => {
    if (!currentUser) { onShowToast('You must be logged in', 'error'); return; }
    if (!formData.brandId) { onShowToast('Select a brand first', 'warning'); return; }
    setModelDrawerOpen(true);
  };

  const handleCategoryCreated = (category: Category) => {
    setCategories(prev => [...prev, category]);
    setFormData(prev => ({ ...prev, categoryId: category.id! }));
  };

  const handleSubcategoryCreated = (category: Category) => {
    setSubcategories(prev => [...prev, category]);
    setFormData(prev => ({ ...prev, subcategoryId: category.id! }));
  };

  const handleBrandCreated = (brand: Brand) => {
    setBrands(prev => [...prev, brand]);
    setFormData(prev => ({ ...prev, brandId: brand.id!, modelId: '', trimId: '' }));
  };

  const handleModelCreated = (carModel: CarModel) => {
    setCarModels(prev => [...prev, carModel]);
    setFormData(prev => ({ ...prev, modelId: carModel.id!, trimId: '' }));
  };

  // ── Confirm dialog helpers ────────────────────────────────────────────────
  const openConfirmDialog = (title: string, message: React.ReactNode, onConfirm: () => Promise<void>) => {
    setConfirmDialog({ open: true, title, message, loading: false, onConfirm });
  };

  const handleConfirmDialogConfirm = async () => {
    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try { await confirmDialog.onConfirm(); }
    finally { setConfirmDialog(CONFIRM_DIALOG_CLOSED); }
  };

  const handleConfirmDialogCancel = () => {
    if (confirmDialog.loading) return;
    setConfirmDialog(CONFIRM_DIALOG_CLOSED);
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.categoryId) {
      loadSubcategories(formData.categoryId);
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, subcategoryId: '' }));
    }
  }, [formData.categoryId]);

  useEffect(() => {
    if (isOpen) {
      setActiveStep(0);

      if (product && (mode === 'edit' || mode === 'view')) {
        setFormData({
          name: product.name, description: product.description || '',
          price: product.price.toString(), discount: (product.discount || 0).toString(),
          categoryId: product.categoryId,
          subcategoryId: product.subcategoryId || '', brandId: product.brandId || '',
          modelId: product.modelId || '', trimId: product.trimId || '',
          sku: product.sku, images: [],
          branchId: product.branchId || '',
          unitCost: product.unitCost?.toString() || '0',
          modelYear: product.modelYear?.toString() || '',
          registrationYear: product.registrationYear?.toString() || '',
          mileageKm: product.mileageKm?.toString() || '',
          fuelType: product.fuelType || '',
          transmission: product.transmission || '',
          engineCapacityCc: product.engineCapacityCc?.toString() || '',
          driveType: product.driveType || '',
          seatingCapacity: product.seatingCapacity?.toString() || '',
          exteriorColor: product.exteriorColor || '',
          interiorColor: product.interiorColor || '',
          condition: product.condition || 'Used - Good',
          status: product.status || 'In Stock',
          registrationNumber: product.registrationNumber || '',
          ownersCount: product.ownersCount?.toString() || '',
          isImported: product.isImported || false,
          importOrderId: product.importOrderId || '',
          countryOfOrigin: product.countryOfOrigin || '',
          features: product.features || [],
        });
        setExistingImages(product.images || []);
      } else {
        resetForm();
        if (initialData) {
          setFormData(prev => ({ ...prev, ...initialData }));
        }
      }
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product, mode]);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadInitialData = async () => {
    try {
      setLoadingData(true);
      const [categoriesData, carModelsData, branchesData, brandsData] = await Promise.all([
        CategoryService.getMainCategories(),
        fetchActiveCarModels(),
        WarehouseService.getActiveWarehouses(),
        fetchActiveBrands(),
      ]);
      setCategories(categoriesData);
      setCarModels(carModelsData);
      setBranches(branchesData);
      setBrands(brandsData);
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setError('Failed to load categories, models, branches, or brands');
      onShowToast('Failed to load categories, models, branches, or brands', 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const data = await CategoryService.getSubcategories(categoryId);
      setSubcategories(data);
    } catch (err: any) {
      console.error('Error loading subcategories:', err);
      onShowToast('Failed to load subcategories', 'error');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM_DATA);
    setExistingImages([]);
    setActiveStep(0);
    setError('');
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBrandSelectChange = (e: any) => {
    // Changing brand invalidates whatever model/trim was picked, since models are brand-scoped.
    setFormData(prev => ({ ...prev, brandId: e.target.value, modelId: '', trimId: '' }));
  };

  const handleModelSelectChange = (e: any) => {
    setFormData(prev => ({ ...prev, modelId: e.target.value, trimId: '' }));
  };

  const handleSwitchChange = (name: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (existingImages.length + formData.images.length + files.length > 5) {
      onShowToast('Maximum 5 images allowed per product', 'warning');
      return;
    }
    setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
  };

  const handleRemoveNewImage = (index: number) => {
    openConfirmDialog('Remove Image', 'Are you sure you want to remove this image?', async () => {
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    });
  };

  const handleReorderExistingImages = (oldIndex: number, newIndex: number) => {
    setExistingImages(prev => arrayMove(prev, oldIndex, newIndex));
  };

  const handleReorderNewImages = (oldIndex: number, newIndex: number) => {
    setFormData(prev => ({ ...prev, images: arrayMove(prev.images, oldIndex, newIndex) }));
  };

  const handleRemoveExistingImage = (imageUrl: string) => {
    if (mode === 'view') return;
    openConfirmDialog('Remove Image', 'Are you sure you want to remove this image? This action cannot be undone.', async () => {
      if (!product?.id) return;
      setLoading(true);
      try {
        await ProductService.removeProductImage(product.id, imageUrl);
        setExistingImages(prev => prev.filter(img => img !== imageUrl));
        onShowToast('Image removed successfully', 'success');
      } catch (err: any) {
        console.error('Error removing image:', err);
        onShowToast('Failed to remove image: ' + (err.message || 'Unknown error'), 'error');
      } finally {
        setLoading(false);
      }
    });
  };

  // ── Derived helpers ───────────────────────────────────────────────────────
  const getDiscountedPrice = () =>
    calculateDiscountedPrice(parseFloat(formData.price) || 0, parseFloat(formData.discount) || 0);

  const getTitle = () => ({ add: 'Add New Car', edit: 'Edit Car', view: 'Car Details' }[mode] || '');
  const getSelectedBrand = (): Brand | undefined => brands.find(b => b.id === formData.brandId);
  const getSelectedBranch = (): Warehouse | undefined => branches.find(w => w.id === formData.branchId);
  const getSelectedTrim = (): Trim | undefined => trimsForModel.find(t => t.id === formData.trimId);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    if (!formData.name.trim()) { setError('Car title is required'); onShowToast('Car title is required', 'error'); return false; }
    if (isRichTextEmpty(formData.description)) { setError('Description is required'); onShowToast('Description is required', 'error'); return false; }
    if (!formData.price || parseFloat(formData.price) <= 0) { setError('Valid price is required'); onShowToast('Valid price is required', 'error'); return false; }
    if (!formData.sku.trim()) { setError('VIN / Chassis No. is required'); onShowToast('VIN / Chassis No. is required', 'error'); return false; }
    const discount = parseFloat(formData.discount) || 0;
    if (discount < 0 || discount > 100) { setError('Discount must be between 0 and 100'); onShowToast('Discount must be between 0 and 100', 'error'); return false; }
    if (!formData.categoryId) { setError('Category is required'); onShowToast('Category is required', 'error'); return false; }
    if (!formData.branchId) { setError('A branch is required — every car belongs to exactly one branch'); onShowToast('A branch is required', 'error'); return false; }
    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (mode === 'view') return;
    if (!validateForm()) return;
    try {
      setLoading(true); setError('');
      const userName = currentUser?.displayName || currentUser?.email || 'Unknown';
      let savedProductId: string | undefined;
      if (mode === 'add') {
        savedProductId = await ProductService.createProduct(formData, currentUser?.uid || '', userName);
        onShowToast('Car created successfully', 'success');
      } else if (mode === 'edit' && product?.id) {
        await ProductService.updateProduct(product.id, formData, existingImages, currentUser?.uid || '', userName);
        savedProductId = product.id;
        onShowToast('Car updated successfully', 'success');
      }
      onSave(savedProductId);
    } catch (err: any) {
      console.error('Error saving car:', err);
      setError(err.message || 'Failed to save car');
      onShowToast(err.message || 'Failed to save car', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Image grid (defined inside so it can close over openLightbox) ─────────
  // When onReorder is provided, tiles become drag-sortable (dnd-kit) via a
  // drag handle in the top-left corner — the id list lets the "new images"
  // grid track File identity across renders even though its preview URLs
  // are regenerated (blob URLs) on every render.
  const ImageTile = ({
    id, src, index, images, onRemove, onReorder, readOnly,
  }: { id: string; src: string; index: number; images: string[]; onRemove?: (index: number) => void; onReorder?: (oldIndex: number, newIndex: number) => void; readOnly: boolean }) => {
    const sortable = useSortable({ id, disabled: !onReorder });
    const style: React.CSSProperties = onReorder ? {
      transform: CSS.Transform.toString(sortable.transform),
      transition: sortable.transition,
      opacity: sortable.isDragging ? 0.5 : 1,
    } : {};

    return (
      <Grid item xs={6} sm={4}>
        <Box
          ref={onReorder ? sortable.setNodeRef : undefined}
          style={style}
          sx={{
            position: 'relative', borderRadius: 1.5, overflow: 'hidden', aspectRatio: '1',
            bgcolor: subtleBg, border: '1px solid', borderColor: dividerColor,
            cursor: 'pointer',
            '&:hover .img-overlay': { opacity: 1 },
            '&:hover img': { transform: 'scale(1.04)' },
          }}
          onClick={() => openLightbox(images, index)}
        >
          <Box component="img" src={src} alt={`Image ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s ease' }} />
          <Box
            className="img-overlay"
            sx={{
              position: 'absolute', inset: 0,
              bgcolor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              opacity: 0, transition: 'opacity 0.2s ease',
            }}
          >
            <Tooltip title="View">
              <Box
                sx={{
                  width: 36, height: 36, borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  '&:hover': { bgcolor: '#ffffff', transform: 'scale(1.08)' },
                  transition: 'transform 0.15s ease',
                }}
              >
                <ZoomInIcon sx={{ fontSize: 20, color: '#212121' }} />
              </Box>
            </Tooltip>
            {!readOnly && onRemove && (
              <Tooltip title="Remove">
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    bgcolor: 'rgba(211,47,47,0.92)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    '&:hover': { bgcolor: '#c62828', transform: 'scale(1.08)' },
                    transition: 'transform 0.15s ease',
                  }}
                  onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                >
                  <DeleteIcon sx={{ fontSize: 18, color: '#ffffff' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          {onReorder && (
            <Tooltip title="Drag to reorder">
              <Box
                {...sortable.attributes}
                {...sortable.listeners}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: 'absolute', top: 6, left: 6,
                  width: 28, height: 28, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'grab', touchAction: 'none',
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: 18, color: '#ffffff' }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Grid>
    );
  };

  const ImageGrid = ({
    images, ids, onRemove, onReorder, readOnly = false,
  }: { images: string[]; objectUrls?: boolean; ids?: string[]; onRemove?: (index: number) => void; onReorder?: (oldIndex: number, newIndex: number) => void; readOnly?: boolean }) => {
    const itemIds = ids || images;
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const tiles = images.map((src, index) => (
      <ImageTile key={itemIds[index]} id={itemIds[index]} src={src} index={index} images={images} onRemove={onRemove} onReorder={!readOnly ? onReorder : undefined} readOnly={readOnly} />
    ));

    if (readOnly || !onReorder) {
      return <Grid container spacing={1.5}>{tiles}</Grid>;
    }

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = itemIds.indexOf(String(active.id));
      const newIndex = itemIds.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) onReorder(oldIndex, newIndex);
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <Grid container spacing={1.5}>{tiles}</Grid>
        </SortableContext>
      </DndContext>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Drawer
        anchor="right" open={isOpen} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '90%', md: 900 }, bgcolor: 'background.default' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 60 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>{getTitle()}</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
            {loadingData ? (
              <Box display="flex" alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading form data...</Typography>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 1.5 }} onClose={() => setError('')}>{error}</Alert>
                )}

                {mode !== 'view' ? (
                  <Stepper activeStep={activeStep} orientation="vertical">

                    {/* ── STEP 1: Basic Information ── */}
                    <Step>
                      <StepLabel>Basic Information</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField fullWidth label="Car Title *" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. 2021 Toyota Aqua Hybrid G Grade" size="small" />
                            </Grid>
                            <Grid item xs={12}>
                              <RichTextEditor
                                label="Description" required value={formData.description} onChange={handleDescriptionChange}
                                placeholder="Enter car description — supports bold, bullet points, tables, headers, and more..."
                                error={error === 'Description is required'}
                                helperText={error === 'Description is required' ? 'Description is required' : undefined}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField fullWidth label={`Price (${currencySymbol}) *`} name="price" type="number" value={formData.price} onChange={handleInputChange} inputProps={{ step: "0.01", min: "0" }} placeholder="0.00" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField fullWidth label="Discount (%)" name="discount" type="number" value={formData.discount} onChange={handleInputChange} inputProps={{ step: "1", min: "0", max: "100" }} placeholder="0" size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start"><DiscountIcon color="action" sx={{ fontSize: 18 }} /></InputAdornment> }}
                              />
                            </Grid>
                            {parseFloat(formData.price) > 0 && parseFloat(formData.discount) > 0 && (
                              <Grid item xs={12}>
                                <Box sx={{ p: 1.5, bgcolor: successBg, border: '1px solid', borderColor: successBorder, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <DiscountIcon color="success" sx={{ fontSize: 18 }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">After {formData.discount}% discount:</Typography>
                                    <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 0.25 }}>
                                      <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{currencySymbol} {formatNum(parseFloat(formData.price))}</Typography>
                                      <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700 }}>{currencySymbol} {formatNum(getDiscountedPrice())}</Typography>
                                      <Chip label={`Save ${currencySymbol} ${formatNum(calculateDiscountAmount(parseFloat(formData.price), parseFloat(formData.discount)))}`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    </Stack>
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                              <TextField fullWidth label="VIN / Chassis No. *" name="sku" value={formData.sku} onChange={handleInputChange} placeholder="e.g. NHP10-1234567" size="small" helperText="Unique per vehicle — this is the one and only unit with this VIN" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label={`Unit Cost (${currencySymbol})`}
                                name="unitCost"
                                type="number"
                                value={formData.unitCost}
                                onChange={handleInputChange}
                                inputProps={{ step: "0.01", min: "0" }}
                                placeholder="0.00"
                                size="small"
                                helperText="Purchase / landed cost for this vehicle"
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Vehicle Specifications</Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Model Year" name="modelYear" type="number" value={formData.modelYear} onChange={handleInputChange} placeholder="2021" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Registration Year" name="registrationYear" type="number" value={formData.registrationYear} onChange={handleInputChange} placeholder="2021" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Mileage (km)" name="mileageKm" type="number" value={formData.mileageKm} onChange={handleInputChange} placeholder="45000" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Registration No." name="registrationNumber" value={formData.registrationNumber} onChange={handleInputChange} placeholder="e.g. CAB-1234" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField select fullWidth label="Fuel Type" name="fuelType" value={formData.fuelType} onChange={handleSelectChange as any} size="small">
                                {FUEL_TYPES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField select fullWidth label="Transmission" name="transmission" value={formData.transmission} onChange={handleSelectChange as any} size="small">
                                {TRANSMISSION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField select fullWidth label="Drive Type" name="driveType" value={formData.driveType} onChange={handleSelectChange as any} size="small">
                                {DRIVE_TYPES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Engine (cc)" name="engineCapacityCc" type="number" value={formData.engineCapacityCc} onChange={handleInputChange} placeholder="1500" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Seating Capacity" name="seatingCapacity" type="number" value={formData.seatingCapacity} onChange={handleInputChange} placeholder="5" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Exterior Color" name="exteriorColor" value={formData.exteriorColor} onChange={handleInputChange} placeholder="Pearl White" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField fullWidth label="Interior Color" name="interiorColor" value={formData.interiorColor} onChange={handleInputChange} placeholder="Black" size="small" />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField select fullWidth label="Condition" name="condition" value={formData.condition} onChange={handleSelectChange as any} size="small">
                                {CAR_CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField
                                select fullWidth label="Availability Status" name="status" value={formData.status}
                                onChange={handleSelectChange as any} size="small" disabled={formData.status === 'Sold'}
                                helperText={formData.status === 'Sold' ? "Sold cars are managed from the Sold Vehicles page" : "Drives availability everywhere — no separate stock flag"}
                              >
                                {CAR_STATUSES.filter(s => s !== 'Sold' || formData.status === 'Sold').map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={4}>
                              <TextField fullWidth label="No. of Previous Owners" name="ownersCount" type="number" value={formData.ownersCount} onChange={handleInputChange} placeholder="1" size="small" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField select fullWidth label="Country of Origin (if imported)" name="countryOfOrigin" value={formData.countryOfOrigin} onChange={handleSelectChange as any} size="small">
                                <MenuItem value=""><em>None</em></MenuItem>
                                {formData.countryOfOrigin && !countries.some(c => c.name === formData.countryOfOrigin) && (
                                  <MenuItem value={formData.countryOfOrigin}>{formData.countryOfOrigin}</MenuItem>
                                )}
                                {countries.map(c => <MenuItem key={c.code} value={c.name}>{c.name}</MenuItem>)}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                              <FormControlLabel
                                control={<Switch checked={formData.isImported} onChange={(e) => handleSwitchChange('isImported', e.target.checked)} />}
                                label="This vehicle was brought in via an Import Order"
                              />
                            </Grid>
                            {formData.importOrderId && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Auto-linked to Import Order {formData.importOrderId} — generated automatically when
                                  that order was placed/received, not editable here.
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </StepContent>
                    </Step>

                    {/* ── STEP 2: Category, Brand, Model/Trim & Branch ── */}
                    <Step>
                      <StepLabel>Category, Brand, Model & Branch</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Main Category *</InputLabel>
                                  <Select name="categoryId" value={formData.categoryId} onChange={handleSelectChange} label="Main Category *">
                                    <MenuItem value="">Select Category</MenuItem>
                                    {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <Tooltip title="Add New Category">
                                  <IconButton onClick={openCategoryDrawer} size="small" color="primary" sx={{ border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, mt: 0.25 }}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <FormControl fullWidth size="small" disabled={!formData.categoryId}>
                                  <InputLabel>Subcategory</InputLabel>
                                  <Select name="subcategoryId" value={formData.subcategoryId} onChange={handleSelectChange} label="Subcategory">
                                    <MenuItem value="">None</MenuItem>
                                    {subcategories.map(sub => <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <Tooltip title={formData.categoryId ? 'Add New Subcategory' : 'Select a main category first'}>
                                  <span>
                                    <IconButton onClick={openSubcategoryDrawer} size="small" color="primary" disabled={!formData.categoryId} sx={{ border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, mt: 0.25 }}>
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Grid>

                            <Grid item xs={12}>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 1 }}>
                                <StorefrontIcon color="action" sx={{ fontSize: 18 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Brand</Typography>
                                <Chip label="Optional" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Brand</InputLabel>
                                  <Select
                                    name="brandId" value={formData.brandId} onChange={handleBrandSelectChange} label="Brand"
                                    renderValue={(selected) => {
                                      if (!selected) return <em style={{ color: textSecondary, fontStyle: 'normal' }}>No Brand</em>;
                                      const brand = brands.find(b => b.id === selected);
                                      if (!brand) return selected as string;
                                      return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {brand.imageUrl && <Avatar src={brand.imageUrl} alt={brand.name} sx={{ width: 20, height: 20, borderRadius: 0.5 }} />}
                                          <Typography variant="body2">{brand.name}</Typography>
                                        </Box>
                                      );
                                    }}
                                  >
                                    <MenuItem value=""><em>No Brand</em></MenuItem>
                                    {brands.map(brand => (
                                      <MenuItem key={brand.id} value={brand.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                          {brand.imageUrl ? (
                                            <Avatar src={brand.imageUrl} alt={brand.name} sx={{ width: 24, height: 24, borderRadius: 0.75 }} />
                                          ) : (
                                            <Avatar sx={{ width: 24, height: 24, borderRadius: 0.75, bgcolor: subtlerBg }}>
                                              <StorefrontIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                            </Avatar>
                                          )}
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>{brand.name}</Typography>
                                            {brand.websiteUrl && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{brand.websiteUrl}</Typography>}
                                          </Box>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Tooltip title="Add New Brand">
                                  <IconButton onClick={openBrandDrawer} size="small" color="primary" sx={{ border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, mt: 0.25 }}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>

                            {formData.brandId && getSelectedBrand() && (
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 1.5, bgcolor: subtleBg, border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {getSelectedBrand()!.imageUrl ? (
                                    <Avatar src={getSelectedBrand()!.imageUrl} alt={getSelectedBrand()!.name} variant="rounded" sx={{ width: 36, height: 36 }} />
                                  ) : (
                                    <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: subtlerBg }}>
                                      <StorefrontIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </Avatar>
                                  )}
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{getSelectedBrand()!.name}</Typography>
                                    {getSelectedBrand()!.websiteUrl && <Typography variant="caption" color="text.secondary">{getSelectedBrand()!.websiteUrl}</Typography>}
                                  </Box>
                                  {getSelectedBrand()!.featured && <Chip label="Featured" size="small" color="warning" sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }} />}
                                </Box>
                              </Grid>
                            )}

                            {brands.length === 0 && (
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                  <Typography variant="body2">No active brands available. You can create brands in the Brands section.</Typography>
                                </Alert>
                              </Grid>
                            )}

                            {/* ── Model & Trim ── */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 0.5 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, mb: 1 }}>
                                <DirectionsCarIcon color="action" sx={{ fontSize: 18 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Model & Trim</Typography>
                                <Chip label="Optional" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <FormControl fullWidth size="small" disabled={!formData.brandId}>
                                  <InputLabel>Model</InputLabel>
                                  <Select name="modelId" value={formData.modelId} onChange={handleModelSelectChange} label="Model">
                                    <MenuItem value=""><em>No Model</em></MenuItem>
                                    {modelsForBrand.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                                  </Select>
                                </FormControl>
                                <Tooltip title={formData.brandId ? 'Add New Model' : 'Select a brand first'}>
                                  <span>
                                    <IconButton onClick={openModelDrawer} size="small" color="primary" disabled={!formData.brandId} sx={{ border: '1px solid', borderColor: dividerColor, borderRadius: 1.5, mt: 0.25 }}>
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small" disabled={!formData.modelId || trimsForModel.length === 0}>
                                <InputLabel>Trim</InputLabel>
                                <Select name="trimId" value={formData.trimId} onChange={handleSelectChange} label="Trim">
                                  <MenuItem value=""><em>No Trim</em></MenuItem>
                                  {trimsForModel.map(t => <MenuItem key={t.id} value={t.id}>{t.name}{t.description ? ` — ${t.description}` : ''}</MenuItem>)}
                                </Select>
                              </FormControl>
                            </Grid>

                            {formData.modelId && !selectedModel && (
                              <Grid item xs={12}>
                                <Alert severity="warning" sx={{ borderRadius: 1.5 }}>Selected model no longer exists — please re-select.</Alert>
                              </Grid>
                            )}

                            {/* ── Branch (single — a car is one physical unit at one branch) ── */}
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1.5 }}>
                                <BranchIcon color="primary" sx={{ fontSize: 18 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Branch *</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Every car is a single physical unit — pick the one branch it's currently at. Availability is tracked via the Status field above, not a stock count.
                              </Typography>
                            </Grid>

                            {activeBranches.length === 0 && (
                              <Grid item xs={12}>
                                <Alert severity="warning" sx={{ borderRadius: 1.5 }}>No active branches available. Please create a branch first.</Alert>
                              </Grid>
                            )}

                            {activeBranches.length > 0 && (
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Branch *</InputLabel>
                                  <Select name="branchId" value={formData.branchId} onChange={handleSelectChange} label="Branch *">
                                    <MenuItem value="">Select Branch</MenuItem>
                                    {activeBranches.map(b => <MenuItem key={b.id} value={b.id}>{b.name} ({b.code})</MenuItem>)}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </StepContent>
                    </Step>

                    {/* ── STEP 3: Images ── */}
                    <Step>
                      <StepLabel>Car Images</StepLabel>
                      <StepContent>
                        <Box sx={{ pt: 2, pb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Upload up to 5 images. Click any image to preview it in full size.
                          </Typography>
                          {existingImages.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}>
                                Current Images ({existingImages.length})
                              </Typography>
                              <ImageGrid images={existingImages} onRemove={(index) => handleRemoveExistingImage(existingImages[index])} onReorder={handleReorderExistingImages} />
                            </Box>
                          )}
                          {formData.images.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 1.5 }}>
                                New Images ({formData.images.length})
                              </Typography>
                              <ImageGrid
                                images={formData.images.map(f => URL.createObjectURL(f))}
                                ids={formData.images.map(f => `${f.name}_${f.size}_${f.lastModified}`)}
                                objectUrls
                                onRemove={handleRemoveNewImage}
                                onReorder={handleReorderNewImages}
                              />
                            </Box>
                          )}
                          {(existingImages.length + formData.images.length) < 5 ? (
                            <Button component="label" variant="outlined" startIcon={<UploadIcon />} size="small">
                              Upload Images
                              <input type="file" hidden multiple accept="image/*" onChange={handleImageUpload} />
                            </Button>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: 1.5 }}>Maximum 5 images reached. Remove existing images to upload new ones.</Alert>
                          )}
                        </Box>
                      </StepContent>
                    </Step>
                  </Stepper>

                ) : (
                  /* ── VIEW MODE ── */
                  <Box>
                    <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                      <Box sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Basic Information</Typography>
                          <Chip label={formData.status || 'In Stock'} size="small" color={formData.status === 'Sold' ? 'default' : 'primary'} variant="outlined" />
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Car Title</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{formData.name}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>Description</Typography>
                            <Box sx={{ p: 2, bgcolor: subtleBg, borderRadius: 1.5, border: '1px solid', borderColor: dividerColor }}>
                              <RichTextViewer value={formData.description} />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Price</Typography>
                            {parseFloat(formData.discount) > 0 ? (
                              <Stack spacing={0.5}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>{currencySymbol} {formatNum(parseFloat(formData.price))}</Typography>
                                  <Chip label={`${formData.discount}% OFF`} size="small" color="success" sx={{ height: 16, fontSize: '0.65rem' }} />
                                </Stack>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{currencySymbol} {formatNum(getDiscountedPrice())}</Typography>
                                <Typography variant="caption" color="text.secondary">Save {currencySymbol} {formatNum(calculateDiscountAmount(parseFloat(formData.price), parseFloat(formData.discount)))}</Typography>
                              </Stack>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{currencySymbol} {formatNum(parseFloat(formData.price))}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>VIN / Chassis No.</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formData.sku}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Unit Cost</Typography>
                            <Typography variant="body2">{currencySymbol} {formatNum(parseFloat(formData.unitCost) || 0)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Branch</Typography>
                            <Typography variant="body2">{getSelectedBranch()?.name || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>

                    <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                      <Box sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Category, Brand & Model</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Main Category</Typography>
                            <Typography variant="body2">{categories.find(c => c.id === formData.categoryId)?.name || 'N/A'}</Typography>
                          </Grid>
                          {formData.subcategoryId && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Subcategory</Typography>
                              <Typography variant="body2">{subcategories.find(c => c.id === formData.subcategoryId)?.name || 'N/A'}</Typography>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Brand</Typography>
                            {formData.brandId && brands.find(b => b.id === formData.brandId) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {brands.find(b => b.id === formData.brandId)!.imageUrl ? (
                                  <Avatar src={brands.find(b => b.id === formData.brandId)!.imageUrl} alt={brands.find(b => b.id === formData.brandId)!.name} variant="rounded" sx={{ width: 28, height: 28 }} />
                                ) : (
                                  <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: subtlerBg }}>
                                    <StorefrontIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  </Avatar>
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{brands.find(b => b.id === formData.brandId)!.name}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No brand assigned</Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Model</Typography>
                            <Typography variant="body2">{selectedModel?.name || 'No model assigned'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Trim</Typography>
                            <Typography variant="body2">{getSelectedTrim()?.name || 'No trim assigned'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>

                    {existingImages.length > 0 && (
                      <Card sx={{ mb: 2.5, borderRadius: 2 }} variant="outlined">
                        <Box sx={{ p: 2.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Car Images ({existingImages.length})</Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Click any image to view in full size</Typography>
                          <ImageGrid images={existingImages} readOnly />
                        </Box>
                      </Card>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {mode === 'view' ? (
            <DrawerFooter>
              <Button onClick={onClose} variant="outlined" size="small">Close</Button>
            </DrawerFooter>
          ) : !loadingData && (
            <DrawerFooter>
              {activeStep > 0 && (
                <Button onClick={() => setActiveStep(activeStep - 1)} variant="outlined" size="small" disabled={loading}>Back</Button>
              )}
              {activeStep < 2 ? (
                <Button
                  onClick={() => setActiveStep(activeStep + 1)}
                  variant="contained"
                  size="small"
                  disabled={
                    activeStep === 0
                      ? (!formData.name.trim() || isRichTextEmpty(formData.description))
                      : (!formData.categoryId || !formData.branchId)
                  }
                >
                  Continue
                </Button>
              ) : (
                <Button onClick={() => handleSubmit()} variant="contained" size="small" disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}>
                  {loading ? 'Saving...' : mode === 'add' ? 'Create Car' : 'Update Car'}
                </Button>
              )}
            </DrawerFooter>
          )}
        </Box>
      </Drawer>

      <ImageLightbox images={lightboxImages} initialIndex={lightboxIndex} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {mode === 'add' ? 'Creating Car…' : 'Updating Car…'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      {/* ────────────────────────────────────────────────────────────────────── */}

      <Dialog open={confirmDialog.open} onClose={handleConfirmDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleConfirmDialogCancel} disabled={confirmDialog.loading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDialogConfirm} disabled={confirmDialog.loading}
            startIcon={confirmDialog.loading ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {confirmDialog.loading ? 'Removing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <QuickAddCategoryDrawer
        open={categoryDrawerOpen}
        onClose={() => setCategoryDrawerOpen(false)}
        userId={currentUser?.uid || ''}
        onCreated={handleCategoryCreated}
        onShowToast={onShowToast}
      />
      <QuickAddCategoryDrawer
        open={subcategoryDrawerOpen}
        onClose={() => setSubcategoryDrawerOpen(false)}
        userId={currentUser?.uid || ''}
        parentId={formData.categoryId}
        parentName={categories.find(c => c.id === formData.categoryId)?.name}
        onCreated={handleSubcategoryCreated}
        onShowToast={onShowToast}
      />
      <QuickAddBrandDrawer
        open={brandDrawerOpen}
        onClose={() => setBrandDrawerOpen(false)}
        userId={currentUser?.uid || ''}
        onCreated={handleBrandCreated}
        onShowToast={onShowToast}
      />
      <QuickAddModelDrawer
        open={modelDrawerOpen}
        onClose={() => setModelDrawerOpen(false)}
        userId={currentUser?.uid || ''}
        brandId={formData.brandId}
        brandName={getSelectedBrand()?.name}
        onCreated={handleModelCreated}
        onShowToast={onShowToast}
      />
    </>
  );
};

export default EnhancedProductDrawer;
